import { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { gridPositionToSquare, getSquareCenter } from './gridHelpers';

/**
 * BoardGrid Component
 * Renders a scalable square grid using D3.js
 *
 * @param {number} rows - Number of rows (5, 8, or 10)
 * @param {number} cols - Number of columns (always equals rows for square boards)
 * @param {number} maxPixels - Maximum width/height in pixels (default 600)
 * @param {Array} snakes - Array of snake objects { start, end }
 * @param {Array} ladders - Array of ladder objects { start, end }
 * @param {number} counterPosition - Current position of the counter (1 to rows*cols)
 * @param {boolean} showNumbers - Whether to show square numbers (default true)
 */
export const BoardGrid = ({
    rows = 10,
    cols = 10,
    maxPixels = 600,
    snakes = [],
    ladders = [],
    counterPosition = null,
    showNumbers = true
}) => {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Calculate responsive dimensions
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.clientWidth;
                const size = Math.min(containerWidth, maxPixels);
                setDimensions({ width: size, height: size });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [maxPixels]);

    // Draw the grid
    useEffect(() => {
        if (!svgRef.current || dimensions.width === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const { width, height } = dimensions;
        const cellWidth = width / cols;
        const cellHeight = height / rows;

        // Create group for the grid
        const gridGroup = svg.append('g').attr('class', 'grid');

        // Draw grid cells with numbers
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const x = col * cellWidth;
                const y = row * cellHeight;

                // Get the square number for this cell
                const squareNumber = gridPositionToSquare(row, col, rows, cols);

                // Draw cell rectangle
                gridGroup.append('rect')
                    .attr('x', x)
                    .attr('y', y)
                    .attr('width', cellWidth)
                    .attr('height', cellHeight)
                    .attr('fill', 'white')
                    .attr('stroke', '#D1D5DB') // Tailwind gray-300
                    .attr('stroke-width', 1);

                // Add square number in top-left corner
                if (showNumbers) {
                    const fontSize = Math.max(8, Math.min(12, cellWidth / 4));
                    gridGroup.append('text')
                        .attr('x', x + 4)
                        .attr('y', y + fontSize + 2)
                        .attr('font-size', fontSize)
                        .attr('fill', '#9CA3AF') // Tailwind gray-400
                        .attr('font-family', 'sans-serif')
                        .text(squareNumber);
                }
            }
        }

        // Create group for snakes and ladders (drawn on top of grid)
        const snakeLadderGroup = svg.append('g').attr('class', 'snakes-ladders');

        // Helper function to generate subtle wavy snake path
        const generateSnakePath = (startPos, endPos) => {
            const dx = endPos.x - startPos.x;
            const dy = endPos.y - startPos.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const numWaves = Math.max(1, Math.floor(distance / 60)); // Fewer waves
            const waveAmplitude = Math.min(8, cellWidth / 8); // Smaller amplitude

            // Generate points along the path with subtle sine wave offset
            const points = [];
            for (let i = 0; i <= 20; i++) {
                const t = i / 20;
                const baseX = startPos.x + dx * t;
                const baseY = startPos.y + dy * t;

                // Calculate perpendicular offset for subtle wave
                const perpX = -dy / distance;
                const perpY = dx / distance;
                const waveOffset = Math.sin(t * numWaves * Math.PI * 2) * waveAmplitude;

                points.push({
                    x: baseX + perpX * waveOffset,
                    y: baseY + perpY * waveOffset
                });
            }

            // Use D3 curve generator
            const lineGenerator = d3.line()
                .x(d => d.x)
                .y(d => d.y)
                .curve(d3.curveCatmullRom.alpha(0.5));

            return lineGenerator(points);
        };

        // Helper function to draw ladder with rungs
        const drawLadder = (group, startPos, endPos) => {
            const dx = endPos.x - startPos.x;
            const dy = endPos.y - startPos.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const ladderWidth = Math.min(10, cellWidth / 5);

            // Calculate perpendicular direction
            const perpX = -dy / distance * ladderWidth;
            const perpY = dx / distance * ladderWidth;

            // Draw two side rails
            group.append('line')
                .attr('x1', startPos.x + perpX)
                .attr('y1', startPos.y + perpY)
                .attr('x2', endPos.x + perpX)
                .attr('y2', endPos.y + perpY)
                .attr('stroke', '#D4A500') // Darker yellow for rails
                .attr('stroke-width', 2.5)
                .attr('stroke-linecap', 'round')
                .attr('opacity', 0.9);

            group.append('line')
                .attr('x1', startPos.x - perpX)
                .attr('y1', startPos.y - perpY)
                .attr('x2', endPos.x - perpX)
                .attr('y2', endPos.y - perpY)
                .attr('stroke', '#D4A500')
                .attr('stroke-width', 2.5)
                .attr('stroke-linecap', 'round')
                .attr('opacity', 0.9);

            // Draw rungs
            const numRungs = Math.max(2, Math.floor(distance / 20));
            for (let i = 1; i < numRungs; i++) {
                const t = i / numRungs;
                const rungX = startPos.x + dx * t;
                const rungY = startPos.y + dy * t;

                group.append('line')
                    .attr('x1', rungX + perpX)
                    .attr('y1', rungY + perpY)
                    .attr('x2', rungX - perpX)
                    .attr('y2', rungY - perpY)
                    .attr('stroke', '#F1C40F')
                    .attr('stroke-width', 2)
                    .attr('stroke-linecap', 'round')
                    .attr('opacity', 0.9);
            }
        };

        // Draw ladders
        ladders.forEach(ladder => {
            const startPos = getSquareCenter(ladder.start, rows, cols, cellWidth, cellHeight);
            const endPos = getSquareCenter(ladder.end, rows, cols, cellWidth, cellHeight);
            drawLadder(snakeLadderGroup, startPos, endPos);
        });

        // Draw snakes (subtle wavy purple lines with arrow at end)
        snakes.forEach(snake => {
            const startPos = getSquareCenter(snake.start, rows, cols, cellWidth, cellHeight);
            const endPos = getSquareCenter(snake.end, rows, cols, cellWidth, cellHeight);

            // Draw snake body
            const snakePath = generateSnakePath(startPos, endPos);
            snakeLadderGroup.append('path')
                .attr('d', snakePath)
                .attr('stroke', '#A26ECC')
                .attr('stroke-width', 4)
                .attr('fill', 'none')
                .attr('stroke-linecap', 'round')
                .attr('opacity', 0.85);

            // Draw circle at start (snake head position)
            snakeLadderGroup.append('circle')
                .attr('cx', startPos.x)
                .attr('cy', startPos.y)
                .attr('r', 5)
                .attr('fill', '#8B5BA8')
                .attr('opacity', 0.9);

            // Draw arrowhead at end to indicate direction
            const angle = Math.atan2(endPos.y - startPos.y, endPos.x - startPos.x);
            const arrowSize = 8;
            snakeLadderGroup.append('polygon')
                .attr('points', `0,-${arrowSize/2} ${arrowSize},0 0,${arrowSize/2}`)
                .attr('transform', `translate(${endPos.x}, ${endPos.y}) rotate(${angle * 180 / Math.PI})`)
                .attr('fill', '#8B5BA8')
                .attr('opacity', 0.9);
        });

        // Draw counter (game piece) on top
        if (counterPosition && counterPosition >= 1 && counterPosition <= rows * cols) {
            const counterGroup = svg.append('g').attr('class', 'counter');
            const counterPos = getSquareCenter(counterPosition, rows, cols, cellWidth, cellHeight);
            const counterRadius = Math.min(cellWidth, cellHeight) / 3;

            // Counter shadow
            counterGroup.append('circle')
                .attr('cx', counterPos.x + 2)
                .attr('cy', counterPos.y + 2)
                .attr('r', counterRadius)
                .attr('fill', 'rgba(0,0,0,0.2)');

            // Counter main body
            counterGroup.append('circle')
                .attr('cx', counterPos.x)
                .attr('cy', counterPos.y)
                .attr('r', counterRadius)
                .attr('fill', '#1F2937') // Tailwind gray-800
                .attr('stroke', '#374151') // Tailwind gray-700
                .attr('stroke-width', 2);

            // Counter shine (highlight)
            counterGroup.append('circle')
                .attr('cx', counterPos.x - counterRadius / 3)
                .attr('cy', counterPos.y - counterRadius / 3)
                .attr('r', counterRadius / 4)
                .attr('fill', 'rgba(255,255,255,0.3)');
        }

    }, [dimensions, rows, cols, showNumbers, snakes, ladders, counterPosition]);

    return (
        <div ref={containerRef} className="w-full" style={{ maxWidth: maxPixels }}>
            <svg
                ref={svgRef}
                width={dimensions.width}
                height={dimensions.height}
                className="border border-gray-300 rounded"
            />
        </div>
    );
};

export default BoardGrid;
